import { FC, useState, useEffect } from "react"
import { useAuth } from "../../auth"
import { toast } from 'react-toastify'
import { CompanyGroupAdmin, SubCompanyAdmin, AccountingAccount, SubAccount, Vessel, SubCatalogue } from "../core/_models"
import { getCompanyGroupAdmins, getSubCompanyAdmins, getAccountingAccountsWithParams, getSubAccounts, updateSubCatalogue, getVesselsByCompany } from "../core/_requests"
import { useProcurementPermissions } from '../core/permissionAccess'
import { PROCUREMENT_PERMS } from '../core/permissions'
import { libraryUiScope } from '../core/procurementLibraryUiScope'
import { procurementTenantCgaId } from '../core/procurementTenantContext'
import SearchableSelect from '../../../shared/components/SearchableSelect'

interface EditSubCatalogueModalProps {
  visible: boolean
  onClose: () => void
  onSubmit: () => void
  subCatalogue: SubCatalogue | null
}

const EditSubCatalogueModal: FC<EditSubCatalogueModalProps> = ({ visible, onClose, onSubmit, subCatalogue }) => {
  const { currentUser } = useAuth()
  const { getScope } = useProcurementPermissions()
  const libraryScope = libraryUiScope(
    getScope,
    PROCUREMENT_PERMS.LIBRARY_SUBCATEGORY_MANAGE,
    PROCUREMENT_PERMS.LIBRARY_SUBCATEGORY_VIEW,
  )
  const libraryScopeAll = libraryScope === 'ALL'
  const libraryScopeCompany = libraryScope === 'COMPANY'
  const tenantCgaId = procurementTenantCgaId(currentUser)
  const effectiveCgaId = libraryScopeCompany ? tenantCgaId : undefined

  const [formData, setFormData] = useState({
    company: '',
    accountingCode: '',
    subaccountId: '',
    name: '',
    vesselId: ''
  })
  const [errors, setErrors] = useState<{ [key: string]: string }>({})
  const [isSubmitting, setIsSubmitting] = useState(false)

  // State for all dropdowns
  const [companies, setCompanies] = useState<CompanyGroupAdmin[]>([])
  const [accountingAccounts, setAccountingAccounts] = useState<AccountingAccount[]>([])
  const [subAccounts, setSubAccounts] = useState<SubAccount[]>([])
  const [isLoadingDropdowns, setIsLoadingDropdowns] = useState(false)
  const [isLoadingSubAccounts, setIsLoadingSubAccounts] = useState(false)
  
  // State for vessels
  const [vessels, setVessels] = useState<Vessel[]>([])
  const [isLoadingVessels, setIsLoadingVessels] = useState(false)

  // Load companies when modal opens (global library scope) or vessels for company-scoped users
  useEffect(() => {
    if (visible && subCatalogue) {
      const subaccountId = subCatalogue.subaccounts && Array.isArray(subCatalogue.subaccounts) && subCatalogue.subaccounts.length > 0
        ? subCatalogue.subaccounts[0].id?.toString() || ''
        : ''
      
      setFormData({
        company: subCatalogue.cgaid?.id?.toString() || '',
        accountingCode: '', // Will be loaded based on company and vessel type
        subaccountId: subaccountId,
        name: subCatalogue.name || '',
        vesselId: subCatalogue.vesselId ? String(subCatalogue.vesselId) : ''
      })
    }
  }, [visible, subCatalogue])

  // Load companies when modal opens (only for superadmin)
  useEffect(() => {
    if (visible && libraryScopeAll) {
      loadCompanies()
    } else if (visible && libraryScopeCompany) {
      loadVesselsForCompany()
    }
  }, [visible, libraryScopeAll, libraryScopeCompany])

  // Load vessels when company is selected (for superadmin)
  useEffect(() => {
    if (formData.company) {
      loadVesselsByCompany()
    } else {
      setVessels([])
    }
  }, [formData.company])

  // Load accounting accounts when company and vessel are selected
  useEffect(() => {
    if (formData.company && formData.vesselId) {
      loadAccountingAccounts(parseInt(formData.company), Number(formData.vesselId))
    } else if (formData.vesselId && !libraryScopeAll) {
      const cgaIdToUse = tenantCgaId
      if (!cgaIdToUse) {
        setAccountingAccounts([])
        return
      }
      loadAccountingAccounts(cgaIdToUse, Number(formData.vesselId))
    } else {
      setAccountingAccounts([])
    }
  }, [formData.company, formData.vesselId, effectiveCgaId, libraryScopeAll, tenantCgaId])

  // Load sub accounts when accounting code is selected
  useEffect(() => {
    // For superadmin, require company, accounting code, and vessel
    if (libraryScopeAll) {
      if (formData.accountingCode && formData.company && formData.vesselId) {
        loadSubAccounts(parseInt(formData.accountingCode))
      } else {
        setSubAccounts([])
      }
    } else {
      // For non-superadmin, only require accounting code and vessel
      if (formData.accountingCode && formData.vesselId) {
        loadSubAccounts(parseInt(formData.accountingCode))
      } else {
        setSubAccounts([])
      }
    }
  }, [formData.accountingCode, formData.company, formData.vesselId])

  const loadCompanies = async () => {
    setIsLoadingDropdowns(true)
    try {
      const companiesData = await getCompanyGroupAdmins()
      setCompanies(companiesData)
    } catch (error) {
      console.error('Error loading companies:', error)
    } finally {
      setIsLoadingDropdowns(false)
    }
  }

  const loadVesselsForCompany = async () => {
    if (!effectiveCgaId) return

    setIsLoadingVessels(true)
    try {
      const companyUsername = (currentUser as any)?.companyGroupAdmin?.username || (currentUser as any)?.username
      
      if (!companyUsername) {
        console.error('Company username not found in current user')
        return
      }

      const vesselsData = await getVesselsByCompany(companyUsername)
      setVessels(vesselsData)
    } catch (error) {
      console.error('Error loading vessels for company:', error)
      setVessels([])
    } finally {
      setIsLoadingVessels(false)
    }
  }

  const loadVesselsByCompany = async () => {
    if (!formData.company) return
    
    setIsLoadingVessels(true)
    try {
      const selectedCompany = companies.find(c => c.id === parseInt(formData.company))
      if (!selectedCompany) {
        console.error('Selected company not found')
        return
      }

      const vesselsData = await getVesselsByCompany(selectedCompany.username)
      setVessels(vesselsData)
    } catch (error) {
      console.error('Error loading vessels by company:', error)
      setVessels([])
    } finally {
      setIsLoadingVessels(false)
    }
  }

  const loadAccountingAccounts = async (cgaId: number, vesselId: number) => {
    setIsLoadingDropdowns(true)
    try {
      const accountsData = await getAccountingAccountsWithParams(cgaId, vesselId)
      setAccountingAccounts(accountsData)
    } catch (error) {
      console.error('Error loading accounting accounts:', error)
      setAccountingAccounts([])
    } finally {
      setIsLoadingDropdowns(false)
    }
  }

  const loadSubAccounts = async (accountId: number) => {
    setIsLoadingSubAccounts(true)
    try {
      let cgaIdToUse: number | undefined

      if (libraryScopeAll) {
        cgaIdToUse = formData.company ? parseInt(formData.company) : undefined
      } else {
        cgaIdToUse = tenantCgaId
      }

      const vesselId = formData.vesselId ? Number(formData.vesselId) : undefined
      const subAccountData = await getSubAccounts(accountId, cgaIdToUse, vesselId)
      setSubAccounts(subAccountData)
    } catch (error) {
      console.error('Error loading sub accounts:', error)
      setSubAccounts([])
    } finally {
      setIsLoadingSubAccounts(false)
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }))
    }
  }

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {}

    // Company validation for superadmin
    if (libraryScopeAll && !formData.company) {
      newErrors.company = 'Company is required'
    }

    if (!formData.accountingCode) newErrors.accountingCode = 'Accounting Code is required'
    if (!formData.subaccountId) newErrors.subaccountId = 'Sub Accounting Code is required'
    if (!formData.name.trim()) newErrors.name = 'Sub Catalogue Name is required'
    if (!formData.vesselId) newErrors.vesselId = 'Vessel is required'
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async () => {
    if (!validateForm() || !subCatalogue) return
    setIsSubmitting(true)
    try {
      let cgaIdToUse: number

      if (libraryScopeAll) {
        if (formData.company) {
          cgaIdToUse = parseInt(formData.company)
        } else {
          toast.error('Please select a company')
          setIsSubmitting(false)
          return
        }
      } else {
        if (!tenantCgaId) {
          toast.error('Unable to resolve company scope for this user')
          setIsSubmitting(false)
          return
        }
        cgaIdToUse = tenantCgaId
      }

      const selectedVessel = vessels.find(v => v.id === Number(formData.vesselId))
      await updateSubCatalogue(subCatalogue.id, {
        name: formData.name,
        vesselId: Number(formData.vesselId),
        vesselType: selectedVessel?.vesselType,
        cgaid: { id: cgaIdToUse },
        subaccounts: [{ id: parseInt(formData.subaccountId) }]
      })
      toast.success('Sub Catalogue updated successfully!')
      await onSubmit()
      handleClose()
    } catch (error: any) {
      console.error('Error updating sub catalogue:', error)
      toast.error('Failed to update sub catalogue')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleClose = () => {
    setFormData({
      company: '',
      accountingCode: '',
      subaccountId: '',
      name: '',
      vesselId: ''
    })
    setErrors({})
    setCompanies([])
    setAccountingAccounts([])
    setSubAccounts([])
    setVessels([])
    onClose()
  }

  if (!visible || !subCatalogue) return null

  return (
    <div className="modal fade show d-flex align-items-center justify-content-center" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)', position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', zIndex: 1050 }}>
      <div className='modal-dialog modal-lg modal-dialog-centered' role='document'>
        <div className='modal-content bg-white' style={{ color: '#181C32' }}>
          <div className='modal-header'>
            <h5 className='modal-title'>Edit Sub Catalogue</h5>
            <button type='button' className='btn-close' onClick={handleClose}></button>
          </div>
          <div className='modal-body'>
            <div className='row g-3'>

              {/* Company dropdown - only for superadmin */}
              {libraryScopeAll && (
                <div className='col-md-6'>
                  <label className='form-label required fw-semibold fs-6 mb-2' style={{ color: '#181C32' }}>Company</label>
                  <SearchableSelect
                    className={`form-select ${errors.company ? 'is-invalid' : ''}`}
                    name='company'
                    value={formData.company}
                    onChange={handleInputChange}
                    disabled={isLoadingDropdowns}
                  >
                    <option value=''>Select Company</option>
                    {companies.map(company => (
                      <option key={company.id} value={company.id}>{company.name}</option>
                    ))}
                  </SearchableSelect>
                  {errors.company && <div className='invalid-feedback'>{errors.company}</div>}
                </div>
              )}

              <div className='col-md-6'>
                <label className='form-label required fw-semibold fs-6 mb-2' style={{ color: '#181C32' }}>Vessel</label>
                <SearchableSelect
                  className={`form-select ${errors.vesselId ? 'is-invalid' : ''}`}
                  name='vesselId'
                  value={formData.vesselId}
                  onChange={handleInputChange}
                  disabled={libraryScopeAll && (!formData.company || isLoadingVessels)}
                >
                  <option value=''>
                    {isLoadingVessels ? 'Loading vessels...' :
                      (libraryScopeAll && !formData.company) ? 'Please select a company first' :
                        'Select Vessel'}
                  </option>
                  {vessels.map(vessel => (
                    <option key={vessel.id} value={vessel.id}>
                      {vessel.fleet_name}
                    </option>
                  ))}
                </SearchableSelect>
                {errors.vesselId && <div className='invalid-feedback'>{errors.vesselId}</div>}
              </div>

              <div className='col-md-6'>
                <label className='form-label required fw-semibold fs-6 mb-2' style={{ color: '#181C32' }}>Accounting Code</label>
                <SearchableSelect
                  className={`form-select ${errors.accountingCode ? 'is-invalid' : ''}`}
                  name='accountingCode'
                  value={formData.accountingCode}
                  onChange={handleInputChange}
                  disabled={isLoadingDropdowns || (libraryScopeAll && (!formData.company || !formData.vesselId))}
                >
                  <option value=''>
                    {isLoadingDropdowns ? 'Loading accounting codes...' :
                      (libraryScopeAll && !formData.company) ? 'Please select a company first' :
                        !formData.vesselId ? 'Please select a vessel first' :
                          'Select Accounting Code'}
                  </option>
                  {accountingAccounts.map(account => (
                    <option key={account.id} value={account.id}>
                      {account.accountCodes}
                    </option>
                  ))}
                </SearchableSelect>
                {errors.accountingCode && <div className='invalid-feedback'>{errors.accountingCode}</div>}
              </div>

              <div className='col-md-6'>
                <label className='form-label required fw-semibold fs-6 mb-2' style={{ color: '#181C32' }}>Sub Accounting Code</label>
                <SearchableSelect
                  className={`form-select ${errors.subaccountId ? 'is-invalid' : ''}`}
                  name='subaccountId'
                  value={formData.subaccountId}
                  onChange={handleInputChange}
                  disabled={isLoadingSubAccounts || !formData.accountingCode || !formData.vesselId || (libraryScopeAll && !formData.company)}
                >
                  <option value=''>
                    {isLoadingSubAccounts ? 'Loading sub accounting codes...' :
                      (libraryScopeAll && !formData.company) ? 'Please select a company first' :
                        !formData.vesselId ? 'Please select a vessel first' :
                          !formData.accountingCode ? 'Please select an accounting code first' :
                            'Select Sub Accounting Code'}
                  </option>
                  {subAccounts.map(subAccount => (
                    <option key={subAccount.id} value={subAccount.id}>
                      {subAccount.subaccountCodes}
                    </option>
                  ))}
                </SearchableSelect>
                {errors.subaccountId && <div className='invalid-feedback'>{errors.subaccountId}</div>}
              </div>

              <div className='col-12'>
                <label className='form-label required fw-semibold fs-6 mb-2' style={{ color: '#181C32' }}>Sub Catalogue Name</label>
                <input
                  type='text'
                  className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                  name='name'
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder='e.g., Electrical Spares Master'
                  style={{ color: '#000' }}
                />
                {errors.name && <div className='invalid-feedback'>{errors.name}</div>}
              </div>

              {/* Show current CGA info for Company Group Admin users */}
              {libraryScopeCompany && effectiveCgaId && (
                <div className='col-12'>
                  <div className='alert alert-info'>
                    <i className='fas fa-info-circle me-2'></i>
                    Sub catalogue will be updated under your Company Group Admin (ID: {effectiveCgaId})
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className='modal-footer'>
            <button type='button' className='btn btn-light btn-sm' onClick={handleClose} disabled={isSubmitting}>Cancel</button>
            <button type='button' className='btn btn_primary' onClick={handleSubmit} disabled={isSubmitting}>
              {isSubmitting ? 'Updating...' : 'Update Sub Catalogue'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default EditSubCatalogueModal
